import React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { brand, radius, shadow } from "../../theme/businessFlowsTheme";
import { eyebrowSx } from "./ContextBand";

// The ways into the work, composed from however many features this instance
// offers (Console -> Features). The old 2x2 grid had a fixed shape and the
// content did not: with two features switched off it showed one hero, one
// small tile and two holes.
//
// One rule instead, which has no holes at any count: the first way in is a
// banner, and the rest are rows in a single card. Four features read as a
// banner and three rows; one reads as a banner alone, and a taller one,
// because it is the only thing there.

const motionOff = { "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } } };

// The banner takes the room the ways in beside it don't need: with three
// rows under it the page is already full, with none it is the whole page.
const leadHeight = (rest) => (rest === 0 ? 320 : rest === 1 ? 268 : 210);

function LeadWay({ icon, title, description, onClick, rest }) {
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        p: { xs: 2.5, md: 3 },
        borderRadius: radius.surface,
        minHeight: { xs: 168, md: leadHeight(rest) },
        bgcolor: brand.inkDeep,
        color: "#fff",
        border: `1px solid ${brand.inkDeep}`,
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: shadow.raised },
        "&:focus-visible": { outline: `3px solid ${brand.signal}`, outlineOffset: 3 },
        ...motionOff,
      }}
    >
      {/* The mark, drawn large and quiet: the feature's own icon as the
          banner's texture, never a stock illustration. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          right: { xs: -28, md: -20 },
          bottom: { xs: -28, md: -24 },
          lineHeight: 0,
          color: brand.signal,
          opacity: 0.1,
          pointerEvents: "none",
          "& svg": { fontSize: { xs: 170, md: rest === 0 ? 300 : 220 } },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", gap: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 44, height: 44, borderRadius: radius.card, display: "grid", placeItems: "center", bgcolor: brand.signal, color: brand.ink, flex: "0 0 auto" }}
        >
          {icon}
        </Box>
        <Box sx={{ mt: "auto", minWidth: 0 }}>
          <Typography component="p" sx={{ ...eyebrowSx, color: "rgba(255,255,255,0.6)" }}>
            Start here
          </Typography>
          <Typography
            component="h3"
            sx={{ mt: 0.5, fontWeight: 700, letterSpacing: "-0.02em", fontSize: { xs: "1.3rem", md: "1.5rem" }, lineHeight: 1.15 }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, mt: 0.75 }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.74)", maxWidth: "48ch" }}>
              {description}
            </Typography>
            <Box
              aria-hidden="true"
              sx={{
                ml: "auto",
                flex: "0 0 auto",
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                border: "1px solid rgba(255,255,255,0.28)",
                color: "#fff",
              }}
            >
              <ArrowForwardRoundedIcon fontSize="small" />
            </Box>
          </Box>
        </Box>
      </Box>
    </ButtonBase>
  );
}

function WayRow({ icon, title, description, onClick, first }) {
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        gap: { xs: 1.5, md: 2 },
        textAlign: "left",
        px: { xs: 2, md: 2.5 },
        py: { xs: 1.75, md: 2 },
        minHeight: 64,
        borderTop: first ? 0 : `1px solid ${brand.hairline}`,
        color: brand.ink,
        transition: "background-color .15s ease",
        "&:hover": { bgcolor: brand.shell },
        "&:focus-visible": { outline: `3px solid ${brand.signal}`, outlineOffset: -3 },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{ width: 38, height: 38, flex: "0 0 auto", borderRadius: radius.card, display: "grid", placeItems: "center", bgcolor: brand.mark, color: brand.ink }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="h3" sx={{ fontWeight: 650, fontSize: "1rem", lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: brand.prose, mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
      <ChevronRightRoundedIcon aria-hidden="true" sx={{ color: brand.muted, flex: "0 0 auto" }} />
    </ButtonBase>
  );
}

export default function WaysIn({ items = [] }) {
  const [lead, ...rest] = items;
  return (
    <Box component="section" aria-labelledby="bf-ways-in" sx={{ minWidth: 0 }}>
      <Typography component="h2" id="bf-ways-in" sx={{ ...eyebrowSx, mb: 1.25 }}>
        Ways in
      </Typography>
      {!lead && (
        <Box sx={{ p: { xs: 2.5, md: 3 }, borderRadius: radius.surface, bgcolor: "#fff", border: `1px solid ${brand.line}` }}>
          <Typography sx={{ fontWeight: 650 }}>Nothing is switched on for this instance yet</Typography>
          <Typography variant="body2" sx={{ color: brand.prose, mt: 0.5, maxWidth: "52ch" }}>
            An administrator turns the explorer, search and the AI services on under Console &rarr; Features. Until
            then there is nothing here to open.
          </Typography>
        </Box>
      )}
      {lead && (
        <Box sx={{ display: "grid", gap: { xs: 1.5, md: 2 } }}>
          <LeadWay {...lead} rest={rest.length} />
          {rest.length > 0 && (
            <Box sx={{ borderRadius: radius.surface, bgcolor: "#fff", border: `1px solid ${brand.line}`, overflow: "hidden" }}>
              {rest.map((item, i) => (
                <WayRow key={item.key} {...item} first={i === 0} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
